import type { ConfigNode, ExecutionStep, WorkflowExecution } from '@/types/imageWorkflow';

export interface PromptExecutionMetadata {
  nodeKey: string;
  mode: string;
  agentType?: string;
  agentName?: string;
  agentModel?: string;
  agentConfigVersion?: number;
  llmModel?: string;
  modelRecorded: boolean;
}

function getWorkflowNodes(execution: WorkflowExecution): ConfigNode[] {
  const definition = execution.workflow_snapshot?.definition;
  return definition?.config?.nodes ?? definition?.nodes ?? [];
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}

function asString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim().length > 0 ? value : undefined;
}

function asNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

export function getPromptExecutionMetadata(
  execution: WorkflowExecution,
  steps: readonly ExecutionStep[],
): PromptExecutionMetadata[] {
  const promptNodes = getWorkflowNodes(execution).filter((node) => node.type === 'prompt');
  const stepByNodeKey = new Map(steps.map((step) => [step.node_key, step]));

  return promptNodes.map((node) => {
    const nodeData = asRecord(node.data);
    const step = stepByNodeKey.get(node.key);
    const output = asRecord(step?.output_data);
    const mode = asString(output.mode) ?? asString(nodeData.input_mode) ?? 'manual';
    const agentModel = asString(output.agent_model);
    const llmModel =
      asString(output.model) ??
      asString(nodeData.llm_model) ??
      asString(execution.workflow_snapshot?.prompt_config?.model);

    return {
      nodeKey: node.key,
      mode,
      agentType:
        asString(output.agent_type) ??
        asString(nodeData.agent_type) ??
        asString(execution.workflow_snapshot?.prompt_config?.agent_type),
      agentName: asString(output.agent_name),
      agentModel,
      agentConfigVersion: asNumber(output.agent_config_version),
      llmModel,
      modelRecorded: mode === 'agent' ? !!agentModel : mode === 'llm' ? !!llmModel : true,
    };
  });
}
